import { useCallback, useEffect, useState } from "react";
import { DynamicIcon } from "../../components/DynamicIcon/DynamicIcon";
import { useUserStore } from "../../contexts/userStore";
import AutomationButtonService from "../../services/AutomationButtonService";
import { throttle } from "../../utils/timer";
import './AutomationButton.css';
import CooldownSpinner from "../../components/CooldownSpinner/CooldownSpinner";
import { v4 as uuidv4 } from 'uuid';
import { publish } from "../../utils/events";
import { clamp } from "../../utils/math";
import { useSSEContext } from "../../contexts/sse";
import { calculateBrightness, changeLightness } from "../../utils/color";
import MqttService from "../../services/MqttService";

const THROTTLE_TIME = 0;

export function AutomationButton({ button }) {
	const { selectedCondominio } = useUserStore();
	const [disabled, setDisabled] = useState(false);
	const [componentId, setComponentId] = useState();
	const { sseReceived } = useSSEContext();

	useEffect(() => {
		const uuid = uuidv4();
		setComponentId(uuid);
	}, []);

	function startCooldown(waitTime, startTime) {
		const intervalId = setInterval(() => {
			if ((Date.now() - startTime) >= waitTime) {
				clearInterval(intervalId);
				setDisabled(false);
				return;
			}
			publish(`automationButtonCooldown-${componentId}`, clamp(0, Infinity, ((Date.now() - startTime) / (button.cooldownTime ?? THROTTLE_TIME))));
		}, 10);
		return { intervalId };
	}

	const throttleRequest = useCallback(throttle(async () => {
		const response = await MqttService.sendCommand({
			origin: "frontEnd",
			action: "sendCommand",
			content: { automation_button_id: button.id },
		});
		return response.status && response.status !== 200;
	}, button.cooldownTime ? Number(button.cooldownTime) : THROTTLE_TIME, {
		start: startCooldown,
		end: (startData) => {
			setDisabled(false);
			if (startData?.intervalId) {
				clearInterval(startData.intervalId);
			}
		},
		lastCall: button.last_sent_at ? (new Date(button.last_sent_at)).getTime() : null,
	}), [componentId, button]);

	const onClick = (e) => {
		setDisabled(true);
		throttleRequest(e);
	}

	function startPrevCooldown(lastSentAt) {
		if (button.cooldownTime && button.last_sent_at) {
			const startTime = lastSentAt.getTime();
			const cooldownTime = button.cooldownTime ? Number(button.cooldownTime) : THROTTLE_TIME;
			const waitTime = clamp(0, Infinity, cooldownTime - Math.abs(Date.now() - startTime));
			if (waitTime > 0) {
				setDisabled(true);
				startCooldown(cooldownTime, startTime);
			}
		}
	}

	useEffect(() => {
		if (sseReceived?.action === 'automationCommandSent' && sseReceived.content.automationButtonId && sseReceived.content.automationButtonId === button.id && !disabled) {
			startPrevCooldown(new Date(sseReceived.content.sentAt));
		}
	}, [sseReceived]);

	useEffect(() => {
		startPrevCooldown(new Date(button.last_sent_at));
	}, [button, componentId]);

	const color = button.color ?? "#007bff";

	return (
		<button
			className="automation-button"
			onClick={onClick}
			disabled={disabled}
			style={{
				'--automationColor': color,
				'--automationRecalculatedBrightness': color
				// '--automationRecalculatedBrightness': calculateBrightness(color) > 0.2 ? changeLightness((calculateBrightness(color) - 0.2) * 100, color) : color
			}}
		>
			<CooldownSpinner
				id={`automationButtonCooldown-${componentId}`}
				cooldownTime={button.cooldownTime}
				style={{ position: 'absolute' }}
			/>
			<div>
				{button.icon && (
					<DynamicIcon iconName={button.icon} size={34} color={'white'} />
				)}
				{button.automation_description}
			</div>
		</button>
	);
}